import { createApp } from 'vue';
import App from './App.vue';
import store from './store';
import Axios from 'axios';
import Oruga from '@oruga-ui/oruga-next';
import { bulmaConfig } from "@oruga-ui/theme-bulma";
import '@/assets/css/global.scss';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import '@fortawesome/fontawesome-pro/css/all.css';
import '@fortawesome/fontawesome-pro/js/all.js';
import momentjs from 'moment';
import { loading } from "@/components";
import { card, userPicture, formatCurrency } from "@/components/webComponents";
import Services from "@/services";
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';
//import "@/services/raygun";
import CKEditor from "@ckeditor/ckeditor5-vue";
import { wcPage, wcSectionView, wcPromotion, loadingOverlay } from "@/components";
import GlobalComponents from "@/services/globalComponents";
import financialReportTool from "@/services/financial";
import VueLazyLoad from "vue3-lazyload";
import Vue3Sanitize from "vue-3-sanitize";
let router;
import("./router").then(async ({ default: data }) => {
    router = await data;
    init();
});
Axios.defaults.baseURL = process.env.VUE_APP_API;
Axios.defaults.withCredentials = true;
Axios.interceptors.response.use((resp) => {
    //console.log("env", store.getters.env);
    //console.log("use", { resp: resp });
    if (resp.status == 302) {
        window.location.href = "/login";
    }
    return resp;
}, async (err) => {
    if (Axios.isCancel(err))
        return Promise.reject(err);
    //console.log("err", { resp: err })
    if (err.response !== undefined && (err.response.status === 401 || err.response.status === 302)) {
        window.location.href = "/login";
    }
    else if (err.response === undefined && err.isAxiosError === true && !err.config?.skipNetworkErrorRedirect && router.currentRoute.value.name !== "login") {
        router.push({ name: "login", query: { returnUrl: router.currentRoute.value.fullPath ?? router.currentRoute.value.path } });
    }
    return Promise.reject(err);
});
let loadingApp = null;
const initLoading = () => {
    loadingApp = createApp(loadingOverlay);
    loadingApp.mount("#app");
};
initLoading();
const init = async () => {
    const app = createApp(App);
    app
        .component("loading", loading)
        .component("fa-icon", FontAwesomeIcon)
        .component("wc-page", wcPage)
        .component("wc-section-view", wcSectionView)
        .component("wc-promotion", wcPromotion);
    app.component("card", card);
    app.component("user-picture", userPicture);
    app.component("formatCurrency", formatCurrency);
    GlobalComponents.setup(app);
    app
        .provide("$http", Axios)
        .provide("$moment", momentjs);
    app
        .use(Oruga, {
        iconPack: 'far',
        iconComponent: 'fa-icon',
        ...bulmaConfig
    })
        .use(Vue3Sanitize)
        .use(CKEditor)
        .use(Services.isframe)
        .use(Services.notification.Success)
        .use(Services.notification.Error)
        .use(Services.notification.Warn)
        .use(Services.pdf, { Axios })
        .use(store)
        .use(Services.jsObjectUtils.isObject)
        .use(Services.jsObjectUtils.deepEquals)
        .use(Services.jsObjectUtils.clean)
        .use(Services.jsonTools)
        .use(Services.confirm)
        .use(Services.popup)
        .use(financialReportTool)
        .use(Services.saveOrDiscard)
        .use(Services.hasSlotContent)
        .use(VueTelInput, {
        mode: 'auto',
        autoFormat: true,
        inputOptions: { placeholder: "Enter a phone number" },
        validCharactersOnly: true
    })
        .use(VueLazyLoad, {})
        .use(Services.debouncer);
    //Use this with the inject pattern instead of using getCurrentInstance from now on.
    app
        .provide("$store", app.config.globalProperties.$store)
        .provide("$oruga", app.config.globalProperties.$oruga);
    const resolvedRouter = await router;
    app.use(resolvedRouter);
    app
        .provide("$router", app.config.globalProperties.$router)
        .provide("$route", app.config.globalProperties.$route);
    app.config.globalProperties.$filters = {
        get moment() {
            return momentjs;
        },
        ActionDate(row) {
            if (row.renewalDate) {
                return momentjs.utc(row.renewalDate).subtract(6, "months").format("M/DD/yyyy");
            }
            else
                return null;
        },
        AssignmentStatus(status) {
            if (status == 'F')
                return "Filled";
            else if (status == "E")
                return "Ended";
            else if (status == 'O')
                return 'Open';
            else if (status == 'S')
                return 'Staffed';
            else if (status == 'H')
                return 'Held';
            else if (status == 'C')
                return 'Canceled';
        },
        AddCommas(number) {
            if (number) {
                try {
                    if (Number.isFinite(number))
                        return number.toLocaleString(undefined, { maximumFractionDigits: 0 });
                    else
                        return Number(number.toString()).toLocaleString(undefined, { maximumFractionDigits: 0 });
                }
                catch (err) {
                    return number;
                }
            }
            return number;
        },
        ArrayOrPipes(value) {
            if (!value || value == "null")
                return null;
            try {
                value = JSON.parse(value);
            }
            catch (err) {
                const x = err;
            }
            if (Array.isArray(value))
                return value;
            else
                return value.split("|");
        },
        ArrayOrPipesString(value) {
            const rtn = this.ArrayOrPipes(value);
            return rtn?.join(", ");
        },
        InveniasBaseUrl() {
            let domain = "https://boydenstaging.invenias.com";
            if (store.getters.env === "production")
                domain = "https://boyden.invenias.com";
            return domain;
        },
        InveniasUrl(id) {
            return this.InveniasBaseUrl() + '/web/companies/' + id;
        },
        Currency(currency, amount) {
            if (!amount)
                return currency + '0';
            return currency + ' ' + this.AddCommas(amount);
        },
        FormatK(currency, amount) {
            return currency + this.AddCommas(amount) + 'K';
        },
        Date(dte) {
            if (dte)
                return momentjs.utc(dte).format("MM/DD/yyyy");
            return dte;
        },
        Percentage(amount, digits) {
            if (!digits)
                digits = 0;
            return new Intl.NumberFormat('en-US', { style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
        },
        GlobalRevenue(revenue) {
            if (Number(revenue) >= 1 && Number(revenue) <= 3) {
                switch (revenue) {
                    case 1:
                        return "< $1B";
                    case 2:
                        return "$1 - $5B";
                    case 3:
                        return "$5B +";
                    default:
                        return null;
                }
            }
            return revenue;
        },
        InvoiceName(invoiceNumber) {
            if (invoiceNumber == 1)
                return 'Initial';
            else if (invoiceNumber == 2)
                return invoiceNumber + 'nd';
            else if (invoiceNumber == 3)
                return invoiceNumber + 'rd';
            else
                return invoiceNumber + 'th';
        },
        OffLimitsDate(status, date) {
            if (status.toLowerCase().indexOf('job active') >= 0)
                return 'Job Active';
            if (date > new Date(2220, 0, 1))
                return 'Off Limits';
            else {
                if (new Date(date) < new Date())
                    return '';
                return momentjs(date).format("M/DD/YYYY");
            }
        },
        OfficeNumber(row) {
            return "(O) " + row.officePhone;
        },
        FaxNumber(row) {
            return "(F) " + row.officeFax;
        },
        MobileNumber(row) {
            return "(M) " + row.mobilePhone;
        },
        revisionName(revisionNumber) {
            if (revisionNumber == 0)
                return 'Original amount';
            else if (revisionNumber == 1)
                return '1st revision';
            else if (revisionNumber == 2)
                return '2nd revision';
            else if (revisionNumber == 3)
                return '3rd revision';
            else
                return revisionNumber + 'th revision';
        },
        SearchType(value) {
            if (value == 'IM')
                return 'Interim Management';
            else if (value == 'Executive')
                return 'Executive Search';
            else if (value == 'Consulting' || value == 'BLM')
                return 'Leadership Consulting';
        },
        UserRole(user) {
            let role = user.Role;
            const percent = user.Percentage;
            if (role == "Developer") {
                role = "D";
            }
            else if (role == "Handler") {
                role = "H";
            }
            return (role + "-" + percent + "%");
        },
        WebAddress(address) {
            if (!address)
                return null;
            if (address.startsWith('http'))
                return address;
            return "https://" + address;
        },
        KebabCase(s) {
            if (!s)
                return "";
            return s
                .replace(/([a-z])([A-Z])/g, '$1-$2')
                .replace(/[\s_]+/g, '-')
                .toLowerCase();
        },
        FormatPhone(phoneNumber) {
            // Remove any non-numeric characters, including spaces
            const digits = phoneNumber.replace(/\s+/g, "").replace(/\D/g, "");
            // Check the length of the digits to determine the format
            let formattedNumber;
            if (digits.length === 10) {
                // Format: (XXX) XXX-XXXX
                formattedNumber = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
            }
            else if (digits.length === 11 && digits.startsWith("1")) {
                // Format: +1 (XXX) XXX-XXXX
                formattedNumber = `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
            }
            else {
                // Return the original input if it doesn't match known formats
                return phoneNumber;
            }
            return formattedNumber;
        },
        FixExternalId(externalId) {
            if (externalId == "")
                externalId = null;
            if (!externalId)
                return externalId;
            if (!(typeof externalId === 'string' || externalId instanceof String))
                return externalId;
            const invalidIds = ["MERGE ISCO", "MIDDLE EAST", "NETHERLANDS"];
            for (const i in invalidIds) {
                const id = invalidIds[i];
                externalId = externalId.replace(id, "");
            }
            const regex = /C\d+/i;
            externalId = externalId.replace(regex, "");
            return externalId;
        }
    };
    loadingApp.unmount();
    app.mount('#app');
    store.dispatch("setLoading", false);
};
//How to customize Oruga: https://github.com/oruga-ui/demo-multiframework/blob/master/src/views/Bulma.vue
